import { ChartListItem, splitRankedChartSeries } from '@frontend/chart/components';
import { ConsumptionRateByProductListChart } from '@frontend/chart/components';
import { Chart } from '@frontend/chart/types';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { StatisticsCard } from '../../shared';
import { useLast30DaysVendingPackageVolumeByProductListChart } from './use-last-30-days-vending-consumption-rate-by-product-list-chart';

export interface Last30DaysVendingPackageVolumeByProductListChartProps {
    accountId: string;
}

const PRODUCT_LIMIT = 5;

const toVelocityItems = (chart: Chart, unknownLabel: string): ChartListItem[] =>
    chart.series.flatMap((series) => {
        const rawName = series.name?.trim();
        if (!rawName || rawName.toLowerCase() === 'default') {
            return [];
        }

        return [
            {
                key: rawName,
                name: rawName.toLowerCase() === 'none' ? unknownLabel : rawName,
                value: series.data.reduce((sum, point) => sum + point.value, 0)
            }
        ];
    });

type ProductVelocityRow = { type: 'section'; label: React.ReactNode } | { type: 'item'; item: ChartListItem; tone: 'success' | 'danger' };

export const Last30DaysVendingPackageVolumeByProductListChart = (props: Last30DaysVendingPackageVolumeByProductListChartProps) => {
    const { formatMessage } = useIntl();
    const { data, isLoading } = useLast30DaysVendingPackageVolumeByProductListChart(props);

    const unknownLabel = formatMessage({
        id: 'Objects.UNKNOWN.DEFAULT',
        description: 'Label for unknown',
        defaultMessage: 'Unknown'
    });

    const { highestItems, lowestItems } = useMemo(() => {
        if (!data) {
            return { highestItems: [], lowestItems: [] };
        }

        const { highest, lowest } = splitRankedChartSeries(data, PRODUCT_LIMIT);
        return {
            highestItems: toVelocityItems(highest, unknownLabel),
            lowestItems: toVelocityItems(lowest, unknownLabel)
        };
    }, [data, unknownLabel]);

    const consumedHeader = formatMessage({
        id: 'platform.statistics.vending.last-30-days-vending-package-volume-by-product-list-chart.consumed',
        description: 'Column header for the consumed quantity in the Product velocity list on the vending dashboard',
        defaultMessage: 'Consumed'
    });

    const productHeader = formatMessage({
        id: 'Objects.PRODUCTS.DEFAULT',
        description: 'Label for products',
        defaultMessage: 'Product'
    });

    const rows = useMemo((): ProductVelocityRow[] => {
        const result: ProductVelocityRow[] = [];

        if (highestItems.length > 0) {
            result.push({
                type: 'section',
                label: (
                    <FormattedMessage
                        id='platform.statistics.vending.last-30-days-vending-package-volume-by-product-list-chart.highest'
                        description='Section heading above the products with the highest consumption on the vending dashboard'
                        defaultMessage='Highest'
                    />
                )
            });
            result.push(...highestItems.map((item) => ({ type: 'item' as const, item, tone: 'success' as const })));
        }

        if (lowestItems.length > 0) {
            result.push({
                type: 'section',
                label: (
                    <FormattedMessage
                        id='platform.statistics.vending.last-30-days-vending-package-volume-by-product-list-chart.lowest'
                        description='Section heading above the products with the lowest consumption on the vending dashboard'
                        defaultMessage='Lowest'
                    />
                )
            });
            result.push(...lowestItems.map((item) => ({ type: 'item' as const, item, tone: 'danger' as const })));
        }

        return result;
    }, [highestItems, lowestItems]);

    return (
        <StatisticsCard
            chartKey={ConsumptionRateByProductListChart.definition.key}
            title={
                <FormattedMessage
                    id='platform.statistics.vending.last-30-days-vending-package-volume-by-product-list-chart.title'
                    description='Title of the vending dashboard lists showing products with the highest and lowest consumption volume over the last 30 days'
                    defaultMessage='Product velocity'
                />
            }
            subtitle={
                <FormattedMessage
                    id='widget.last-30-days'
                    defaultMessage='last 30 days'
                />
            }
            isLoading={isLoading}
            hasData={highestItems.length > 0 || lowestItems.length > 0}>
            <div className='min-h-0 flex-1 overflow-y-auto'>
                <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                    <div className='w-full px-4 py-3'>{productHeader}</div>
                    <div className='w-[120px] px-4 py-3 text-right'>{consumedHeader}</div>
                </div>
                {rows.map((row, index) =>
                    row.type === 'section' ? (
                        <div
                            key={`section-${index}`}
                            className='border-border-default text-foreground-default border-b px-4 py-3 text-base font-semibold'>
                            {row.label}
                        </div>
                    ) : (
                        <div
                            key={row.item.key}
                            className='border-border-default flex h-11 items-center gap-4 border-b'>
                            <div className='text-foreground-default flex w-full items-center px-4 text-sm font-medium'>{row.item.name}</div>
                            <div className='flex w-[120px] justify-end pe-4'>
                                <span
                                    className={`text-background-surface inline-flex min-w-8 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold ${
                                        row.tone === 'success' ? 'bg-status-success' : 'bg-status-danger'
                                    }`}>
                                    {row.item.value}
                                </span>
                            </div>
                        </div>
                    )
                )}
            </div>
        </StatisticsCard>
    );
};
